import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { useSEO } from "@/lib/useSEO";
import { ArrowUpRight, Palette, Printer, Globe, Package, Check } from "lucide-react";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);

  useSEO({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const safeGet = (url, setter, transform = (d) => d) => api.get(url)
        .then(r => { if (!cancelled) setter(transform(r.data)); })
        .catch(err => logger.error(`Load ${url} failed:`, err));
      await Promise.all([
        safeGet("/products", setProducts, d => d.slice(0, 6)),
        safeGet("/portfolio", setPortfolio, d => d.slice(0, 3)),
        safeGet("/industries", setIndustries),
        safeGet("/testimonials", setTestimonials),
        safeGet("/faqs", setFaqs, d => d.slice(0, 5)),
      ]);
    };
    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden bg-[#0a0a0a]">
        <img src="https://images.pexels.com/photos/6620972/pexels-photo-6620972.jpeg" alt="Print" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/50 to-transparent" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pb-20 md:pb-28 w-full grid grid-cols-12 gap-8 fade-up">
          <div className="col-span-12 md:col-span-8">
            <p className="text-xs font-mono tracking-[0.4em] text-[#C5A059] uppercase mb-6">A family printing tradition</p>
            <h1 className="font-serif text-[#FAFAFA] text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
              Design.<br />Print.<br /><span className="italic text-[#C5A059]">Deliver.</span>
            </h1>
            <p className="mt-8 text-lg text-[#DADADA] max-w-xl">Premium printing, graphic design, and website craft — built on a family printing tradition dating back to the 1970s.</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/shop" data-testid="hero-shop-btn" className="inline-flex items-center gap-2 bg-[#C5A059] text-[#0a0a0a] hover:bg-[#E3CD96] transition-colors px-8 py-4 text-xs tracking-[0.2em] uppercase font-bold">
                Explore the Shop <ArrowUpRight size={16} />
              </Link>
              <Link to="/quote" data-testid="hero-quote-btn" className="inline-flex items-center gap-2 border border-[#FAFAFA] text-[#FAFAFA] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors px-8 py-4 text-xs tracking-[0.2em] uppercase font-bold">
                Request a Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-y border-[#E5E5E5] py-6 overflow-hidden bg-white">
        <div className="marquee-track flex whitespace-nowrap gap-16 font-serif italic text-4xl md:text-5xl text-[#0a0a0a]">
          {[...Array(4)].map((_, i) => (
            <span key={`marquee-${i}`} className="inline-flex items-center gap-16">
              Design. <span className="text-[#C5A059]">Print.</span> Deliver. A <span className="text-[#C5A059]">family tradition</span>. Design. <span className="text-[#C5A059]">Print.</span> Deliver.
            </span>
          ))}
        </div>
      </div>

      {/* Services */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-12 gap-6 mb-14">
          <p className="col-span-12 md:col-span-3 text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">01 — Services</p>
          <h2 className="col-span-12 md:col-span-9 font-serif text-4xl md:text-6xl tracking-tight leading-none">Three studios,<br /><span className="italic">one obsession</span> — craft.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { to: "/services", label: "Printing", img: "https://images.unsplash.com/photo-1718670013921-2f144aba173a", desc: "Business cards, flyers, menus & more." },
            { to: "/services", label: "Graphic Design", img: "https://images.pexels.com/photos/7318941/pexels-photo-7318941.jpeg", desc: "Logos, brand identity, social kits." },
            { to: "/services", label: "Website Design", img: "https://images.pexels.com/photos/196646/pexels-photo-196646.jpeg", desc: "Business sites, landing pages, redesign." },
          ].map((c, i) => (
            <Link key={c.label} to={c.to} data-testid={`cat-${c.label.toLowerCase().replace(/\s+/g,'-')}`} className="group block relative overflow-hidden bg-[#FAFAFA] border border-[#E5E5E5] gold-border-hover">
              <div className="aspect-[4/5] overflow-hidden">
                <img src={c.img} alt={c.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-8">
                <p className="text-[10px] font-mono tracking-[0.3em] text-[#C5A059]">0{i+1}</p>
                <h3 className="font-serif text-3xl mt-1">{c.label}</h3>
                <p className="text-sm text-[#555] mt-2">{c.desc}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-xs font-mono tracking-[0.25em] uppercase text-[#0a0a0a] group-hover:text-[#C5A059]">Explore <ArrowUpRight size={14} /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      <section className="bg-[#FAFAFA] py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-14">
            <div>
              <p className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059] mb-4">02 — Featured</p>
              <h2 className="font-serif text-4xl md:text-5xl">Signature products.</h2>
            </div>
            <Link to="/shop" data-testid="home-view-all" className="hidden md:inline text-xs tracking-[0.2em] uppercase font-bold border-b border-[#0a0a0a] pb-1 hover:text-[#C5A059] hover:border-[#C5A059]">View All →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {products.map(p => (
              <Link key={p.id} to={`/product/${p.slug}`} data-testid={`product-${p.slug}`} className="group block bg-white border border-[#E5E5E5] gold-border-hover">
                <div className="aspect-square overflow-hidden bg-[#F5F5F5]">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5">
                  <h3 className="font-serif text-xl">{p.name}</h3>
                  <p className="font-mono text-sm mt-2 text-[#C5A059]">${p.base_price.toFixed(2)}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      {industries.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-6 mb-14">
            <p className="col-span-12 md:col-span-3 text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">03 — Industries</p>
            <h2 className="col-span-12 md:col-span-9 font-serif text-4xl md:text-6xl">Who we serve.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map(ind => (
              <Link key={ind.id} to="/industries" data-testid={`industry-${ind.slug}`} className="group block border border-[#E5E5E5] gold-border-hover">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={ind.image_url} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt={ind.name} />
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-2xl">{ind.name}</h3>
                  <p className="text-sm text-[#555] mt-2">{ind.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Portfolio */}
      {portfolio.length > 0 && (
        <section className="bg-[#0a0a0a] text-[#FAFAFA] py-24 md:py-32">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="grid grid-cols-12 gap-6 mb-14">
              <p className="col-span-12 md:col-span-3 text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">04 — Recent Projects</p>
              <h2 className="col-span-12 md:col-span-9 font-serif text-4xl md:text-6xl">Case work.</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {portfolio.map(p => (
                <div key={p.id} className="group overflow-hidden bg-[#1a1a1a] border border-[#222]">
                  <div className="aspect-[4/5] overflow-hidden"><img src={p.image_url} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt={p.title} /></div>
                  <div className="p-6">
                    <p className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#C5A059]">{p.category.replace("_"," ")}</p>
                    <h3 className="font-serif text-2xl mt-2">{p.title}</h3>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/portfolio" data-testid="home-portfolio-all" className="mt-12 inline-flex items-center gap-2 border border-[#FAFAFA] px-8 py-4 text-xs tracking-[0.25em] uppercase font-bold hover:border-[#C5A059] hover:text-[#C5A059]">View All Work <ArrowUpRight size={14} /></Link>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-12 gap-6 mb-16">
          <p className="col-span-12 md:col-span-3 text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">05 — How It Works</p>
          <h2 className="col-span-12 md:col-span-9 font-serif text-4xl md:text-6xl">A simple, careful process.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { icon: Palette, step: "01", title: "Design or Choose", desc: "Pick a stock product or request a custom quote." },
            { icon: Package, step: "02", title: "Upload Artwork", desc: "PDF, PNG, JPG, SVG, AI, EPS, PSD, ZIP — up to 50MB." },
            { icon: Check, step: "03", title: "Approve Proof", desc: "Nothing prints until you sign off on the digital proof." },
            { icon: Printer, step: "04", title: "Print & Deliver", desc: "Produced in-house, packaged with care, shipped to you." },
          ].map(({ icon: Icon, step, title, desc }) => (
            <div key={step} className="border border-[#E5E5E5] p-8 gold-border-hover" data-testid={`how-step-${step}`}>
              <Icon size={28} strokeWidth={1.2} className="text-[#C5A059]" />
              <p className="text-xs font-mono tracking-[0.3em] text-[#C5A059] mt-6">{step}</p>
              <h3 className="font-serif text-2xl mt-2">{title}</h3>
              <p className="text-sm text-[#555] mt-3">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="bg-[#FAFAFA] py-24 md:py-32">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="grid grid-cols-12 gap-6 mb-16">
              <p className="col-span-12 md:col-span-3 text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">06 — Words</p>
              <h2 className="col-span-12 md:col-span-9 font-serif text-4xl md:text-6xl">From the studio's clients.</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map(t => (
                <div key={t.id} className="border border-[#E5E5E5] bg-white p-8" data-testid={`testimonial-${t.id}`}>
                  <p className="text-[#C5A059] font-serif text-4xl leading-none">“</p>
                  <p className="text-lg font-serif italic text-[#0a0a0a] mt-4 leading-snug">{t.quote}</p>
                  <div className="mt-8 flex items-center gap-3">
                    {t.image_url && <img src={t.image_url} className="w-12 h-12 object-cover rounded-none" alt={t.author} />}
                    <div>
                      <p className="font-serif text-lg">{t.author}</p>
                      <p className="text-xs font-mono uppercase tracking-widest text-[#888]">{t.role}{t.company ? ` · ${t.company}` : ""}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ preview */}
      {faqs.length > 0 && (
        <section className="max-w-[1000px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-6 mb-14">
            <p className="col-span-12 md:col-span-4 text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">07 — FAQ</p>
            <h2 className="col-span-12 md:col-span-8 font-serif text-4xl md:text-6xl">Common questions.</h2>
          </div>
          <div className="divide-y divide-[#E5E5E5] border-t border-b border-[#E5E5E5]">
            {faqs.map(f => (
              <details key={f.id} className="group py-6" data-testid={`faq-${f.id}`}>
                <summary className="cursor-pointer flex items-start justify-between gap-6 list-none">
                  <span className="font-serif text-xl md:text-2xl">{f.question}</span>
                  <span className="text-[#C5A059] font-mono text-2xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-[#555] mt-4 leading-relaxed">{f.answer}</p>
              </details>
            ))}
          </div>
          <div className="mt-8"><Link to="/faq" data-testid="home-faq-all" className="text-xs tracking-[0.25em] uppercase font-bold border-b border-[#0a0a0a] pb-1 hover:text-[#C5A059] hover:border-[#C5A059]">All Questions →</Link></div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-[#0a0a0a] text-[#FAFAFA] py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-8">
            <p className="text-xs font-mono tracking-[0.4em] text-[#C5A059] uppercase mb-6">Ready when you are</p>
            <h2 className="font-serif text-5xl md:text-7xl leading-[0.95]">Something remarkable<br /><span className="italic">deserves</span> remarkable print.</h2>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Link to="/quote" data-testid="cta-quote-btn" className="inline-flex items-center gap-2 bg-[#C5A059] text-[#0a0a0a] hover:bg-[#E3CD96] transition-colors px-10 py-5 text-xs tracking-[0.25em] uppercase font-bold">Start a Project <ArrowUpRight size={16} /></Link>
          </div>
        </div>
      </section>
    </>
  );
}
